import { Box, Button, Center, Spinner, Stack, useToast } from "@chakra-ui/react";
import { useMemo } from "react";
import { useParams } from "react-router";

import { dayjs } from "@equidefi/i18n";
import { extractErrorMessage } from "@equidefi/portals/helpers/errors";
import { useInvestmentInquiries, useResumeInquiryUrl, useSendInquiryEmail } from "@equidefi/portals/hooks/useInquiries";
import { useGetInvestment } from "@equidefi/portals/hooks/useInvestments";
import { useCurrentUser } from "@equidefi/portals/hooks/useUsers";
import {
  INQUIRY_APPROVED_STATUSES,
  INQUIRY_FAILED_STATUSES,
  INQUIRY_PENDING_STATUSES,
  INQUIRY_TYPE,
} from "@equidefi/shared";
import type { DTO } from "@equidefi/shared/types";
import { BannerAlert, Heading, Text } from "@equidefi/ui";

import { VAULT_ROUTES } from "@/constants/routes";
import { VaultContainer } from "../../../components";
import { InquiryCard, InvestmentTitleCard, VaultPageHeader } from "../../components";

const isPending = (status?: string) => !!status && INQUIRY_PENDING_STATUSES.some((s) => s === status);
const isApproved = (status?: string) => !!status && INQUIRY_APPROVED_STATUSES.some((s) => s === status);
const isFailed = (status?: string) => !!status && INQUIRY_FAILED_STATUSES.some((s) => s === status);

type RowActionsProps = {
  inquiry: DTO.TInquiry;
  investmentId: string;
  userId: string;
  isOwner: boolean;
  citizenship: string;
  inquiryType: string;
};

const InquiryRowActions = ({ inquiry, investmentId, userId, isOwner, citizenship, inquiryType }: RowActionsProps) => {
  const toast = useToast();
  const resumeMutation = useResumeInquiryUrl(inquiry.inquiry_id);
  const reminderMutation = useSendInquiryEmail(inquiry.inquiry_id, investmentId);

  const pending = isPending(inquiry?.status);
  const failed = isFailed(inquiry?.status);
  if (!pending && !failed) return null;
  if (failed && !isOwner) return null;

  const handleResume = async () => {
    try {
      const result = await resumeMutation.mutateAsync({
        userId,
        citizenship,
        inquiryType,
      });
      const link = (result as { link?: string })?.link;
      if (link) window.location.assign(link);
    } catch (e) {
      toast({
        status: "error",
        description: extractErrorMessage(e, "Could not resume verification."),
      });
    }
  };

  const handleReminder = async () => {
    try {
      await reminderMutation.mutateAsync({
        userId,
        citizenship,
        inquiryType,
      });
      toast({ status: "success", description: "Reminder sent." });
    } catch (e) {
      toast({
        status: "error",
        description: extractErrorMessage(e, "Could not send reminder."),
      });
    }
  };

  if (failed) {
    return (
      <Button onClick={handleResume} isLoading={resumeMutation.isLoading} colorScheme="purple" w="full" h="12">
        Retry Verification
      </Button>
    );
  }

  if (isOwner) {
    return (
      <Button onClick={handleResume} isLoading={resumeMutation.isLoading} colorScheme="purple" w="full" h="12">
        Resume Verification
      </Button>
    );
  }

  return (
    <Button onClick={handleReminder} isLoading={reminderMutation.isLoading} colorScheme="purple" w="full" h="12">
      Send Reminder
    </Button>
  );
};

type InquiryPersonListPageProps = {
  isKYB: boolean;
};

const InquiryPersonListPage = ({ isKYB }: InquiryPersonListPageProps) => {
  const { id: investmentId, userId } = useParams<{
    id: string;
    userId: string;
  }>();
  const inquiryType = isKYB ? INQUIRY_TYPE.KYB : INQUIRY_TYPE.KYC;

  const { data: inquiriesData, isFetched } = useInvestmentInquiries(investmentId as string);
  const { data: investment, isFetched: isInvestmentFetched } = useGetInvestment(investmentId as string);
  const { data: currentUser } = useCurrentUser();

  const inquiries: DTO.TInquiry[] = inquiriesData?.data ?? [];
  const userInquiries = useMemo(
    () => inquiries.filter((i) => i.user_id === userId && i.type === inquiryType),
    [inquiries, userId, inquiryType],
  );
  const activeInquiry = userInquiries[0] ?? null;

  if (!isFetched || !isInvestmentFetched) {
    return (
      <Center h="100vh">
        <Spinner />
      </Center>
    );
  }

  const isOwner = currentUser?.id === userId;
  const subject = isKYB ? "entity" : "individual";
  const pageTitle = isKYB ? "Entity Verification Details" : "Identity Check Details";
  const citizenship = investment?.vault?.citizenship ?? "US_CITIZEN";

  const renderBanner = () => {
    if (!activeInquiry) return null;
    if (isApproved(activeInquiry.status)) {
      return (
        <BannerAlert
          status="success"
          description={`${isKYB ? "Entity Verification" : "Identity Check"} for this ${subject} has been approved.`}
        />
      );
    }
    if (isPending(activeInquiry.status)) {
      return (
        <BannerAlert
          status="warning"
          description={`${isKYB ? "Entity Verification" : "Identity Check"} for this ${subject} is still pending.`}
        />
      );
    }
    if (isFailed(activeInquiry.status)) {
      return (
        <BannerAlert
          status="error"
          description={`${isKYB ? "Entity Verification" : "Identity Check"} for this ${subject} could not be completed. ${
            isOwner ? "Retry below or contact support." : "Ask this person to retry, or contact support."
          }`}
        />
      );
    }
    return null;
  };

  const issuer = investment?.issuer;
  const offering = investment?.offering;

  return (
    <VaultContainer pb="10" maxW={{ base: "560px", lg: "1040px" }}>
      <VaultPageHeader
        crumbs={[
          { label: "Portfolio", to: "/vault/dashboard" },
          { label: issuer?.name ?? "Issuer", to: VAULT_ROUTES.offering(offering?.id ?? "") },
          { label: offering?.name ?? "Offering", to: VAULT_ROUTES.offering(offering?.id ?? "") },
          { label: "Investment", to: VAULT_ROUTES.investmentDetails(investmentId as string) },
          { label: "Identity Verification", to: VAULT_ROUTES.inquiry(investmentId as string) },
          { label: pageTitle },
        ]}
        backTo={VAULT_ROUTES.inquiry(investmentId as string)}
        backLabel="Back To Identity Checks"
      />

      <InvestmentTitleCard investment={investment} issuer={issuer} offering={offering} />

      <Box bg="white" borderRadius="md" boxShadow="md" p="4" mt="4">
        <Heading fontWeight="bold" as="h1" mb="3">
          {pageTitle}
        </Heading>

        {userInquiries.length === 0 ? (
          <Text color="equidefi.blue" mb="0">
            No inquiries have been started for this {subject} yet.
          </Text>
        ) : (
          <Stack spacing="4">
            {renderBanner()}

            <Text textStyle="body2" mb="0">
              You can tap any card below to view more details about that inquiry.
            </Text>

            <Stack spacing="2.5">
              {userInquiries.map((inquiry) => (
                <InquiryCard
                  key={inquiry.id}
                  to={VAULT_ROUTES.inquiryDetails(investmentId as string, inquiry.id)}
                  title={inquiry.display_name}
                  subtitle={dayjs(inquiry.create_date).format("MMM Do, YYYY")}
                  status={inquiry.status}
                  actions={
                    <InquiryRowActions
                      inquiry={inquiry}
                      investmentId={investmentId as string}
                      userId={userId as string}
                      isOwner={isOwner}
                      citizenship={citizenship}
                      inquiryType={inquiryType}
                    />
                  }
                />
              ))}
            </Stack>
          </Stack>
        )}
      </Box>
    </VaultContainer>
  );
};

export default InquiryPersonListPage;
